import { useEffect } from "react";
import { DateTime } from "luxon";
import { modules, entities, currencyToCents, zeroCents } from "byzantine";
import { useInvoiceFlow } from "./InvoiceFlowContext";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";

// tracked at module (not component) scope so a remount of DetailsStep (e.g.
// navigating to review then back to details) doesn't re-run the prefill and
// stomp edits the member already made - a genuinely new invoice has a new uuid,
// so it still prefills normally
const PREFILLED_INVOICE_ID_CACHE_LIMIT = 50;
const prefilledInvoiceIds = (() => {
  const set = new Set<string>();
  const order: string[] = [];

  return {
    has: (id: string) => set.has(id),
    add: (id: string) => {
      if (set.has(id)) return;

      set.add(id);
      order.push(id);

      if (order.length > PREFILLED_INVOICE_ID_CACHE_LIMIT) {
        const oldest = order.shift();
        if (oldest) {
          set.delete(oldest);
        }
      }
    },
  };
})();

const useInvoicePrefill = () => {
  const { invoice } = useInvoiceFlow();
  const institution = useInstitutionSettings();
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const { setFieldValue } = form;

  modules.achPayments.useApplyAchPaymentSettingsDefaults();

  useEffect(() => {
    if (!invoice || prefilledInvoiceIds.has(invoice.uuid)) return;
    if (!entities.invoices.isInvoiceReady(invoice)) return;
    prefilledInvoiceIds.add(invoice.uuid);

    const defaults = entities.invoices.getInvoiceDetailsFormDefaults(
      invoice.extracted
    );
    const earliestDate = modules.achPayments.getStartDate(
      institution.ach_next_cutoff,
      institution.time_zone
    );

    // the form provider is mounted above the steps, so its values outlive a
    // single invoice. each field below is always written — extracted value when
    // there is one, baseline otherwise — so a field the current invoice cannot
    // fill can never keep the previous invoice's value.

    // the extracted value is a plain decimal string ("1234.56"); the form holds
    // cents, so it has to go through the same conversion the amount input uses
    setFieldValue(
      "amount",
      defaults.amount ? currencyToCents(defaults.amount) : zeroCents,
      true
    );

    // only prefill date when it still clears the cutoff, otherwise fall back to
    // the earliest valid date
    const invoiceDate = defaults.date
      ? DateTime.fromFormat(defaults.date, "MM/dd/yyyy")
      : null;
    const earliest = DateTime.fromFormat(earliestDate, "MM/dd/yyyy");
    const canUseInvoiceDate =
      !!defaults.date &&
      !!invoiceDate?.isValid &&
      earliest.isValid &&
      invoiceDate >= earliest;
    setFieldValue(
      "startDate",
      canUseInvoiceDate ? defaults.date : earliestDate,
      true
    );

    setFieldValue("description", defaults.memo || "", true);
  }, [
    invoice,
    setFieldValue,
    institution.ach_next_cutoff,
    institution.time_zone,
  ]);
};

export default useInvoicePrefill;
